import React, { useState, useRef } from 'react';
import { Star, Megaphone, Cutlery, Notes, Search, Cart, User } from 'iconoir-react';
import BannerCarousel from './BannerCarousel';
import ProdutoCarousel from './ProdutoCarousel';
import { formatPrice } from '../utils/currency';
import './TabletLayout.css';

function TabletLayout({ 
  categorias = [], 
  banners = [], 
  destaques = [],
  loja,
  mesa,
  itensCarrinho = 0,
  onProdutoClick, 
  onCarrinhoClick,
  onMeusPedidosClick,
  calculatePrice,
  isDelivery = false
}) {
  const [sidebarView, setSidebarView] = useState(banners.length > 0 ? 'promocoes' : 'cardapio');
  const [categoriaAtiva, setCategoriaAtiva] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const contentRef = useRef(null);
  const destaquesRef = useRef(null);
  const cardapioRef = useRef(null);
  const categoriasRefs = useRef({});

  const isTablet = window.innerWidth >= 768;
  const tipoDevice = isTablet ? 'tablet' : 'mobile';
  const bannersDoDevice = banners.filter(b => b.tipo === tipoDevice).sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  const getImageUrl = (produto) => {
    if (produto.imagem?.imagem) {
      return `https://grubupload.s3.amazonaws.com/${produto.imagem.imagem}`;
    }
    if (produto.imagem && typeof produto.imagem === 'string') {
      return `https://grubupload.s3.amazonaws.com/${produto.imagem}`;
    }
    return 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
  };

  const scrollToDestaques = () => {
    setSidebarView('cardapio');
    setCategoriaAtiva(null);
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToCardapio = () => {
    setSidebarView('cardapio');
    setCategoriaAtiva(null);
    setTimeout(() => {
      if (cardapioRef.current && contentRef.current) {
        const top = cardapioRef.current.offsetTop - 10;
        contentRef.current.scrollTo({ top, behavior: 'smooth' });
      }
    }, 50);
  };

  const scrollToCategoria = (catId) => {
    setSidebarView('cardapio');
    setCategoriaAtiva(catId);
    setTimeout(() => {
      const el = categoriasRefs.current[catId];
      if (el && contentRef.current) {
        const top = el.offsetTop - 70;
        contentRef.current.scrollTo({ top, behavior: 'smooth' });
      }
    }, 50);
  };

  const categoriasVisiveis = categorias.filter(cat => {
    const produtos = cat.produtos || [];
    if (!searchTerm) return produtos.length > 0;
    return produtos.some(p => 
      p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="tablet-layout">
      {/* Sidebar */}
      <aside className="tablet-sidebar">
        <div className="tablet-sidebar-logo">
          <img
            src={loja?.logo_url ? `https://grubupload.s3.amazonaws.com/${loja.logo_url}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
            alt={loja?.nome || 'Logo'}
            className="tablet-logo-img"
          />
          <span className="tablet-logo-nome">{loja?.nome || ''}</span>
        </div>

        <div className="tablet-sidebar-nav">
          {bannersDoDevice.length > 0 && (
            <button
              className={`tablet-nav-btn ${sidebarView === 'promocoes' ? 'active' : ''}`}
              onClick={() => setSidebarView('promocoes')}
            >
              <Megaphone />
              <span>PROMOÇÕES</span>
            </button>
          )}
          {destaques && destaques.length > 0 && (
            <button
              className={`tablet-nav-btn ${sidebarView === 'cardapio' && !categoriaAtiva ? 'active' : ''}`}
              onClick={scrollToDestaques}
            >
              <Star />
              <span>DESTAQUES</span>
            </button>
          )}
          <button
            className={`tablet-nav-btn ${sidebarView === 'cardapio' && categoriaAtiva ? 'active' : ''}`}
            onClick={scrollToCardapio}
          >
            <Cutlery />
            <span>CARDÁPIO</span>
          </button>
        </div>

        {sidebarView === 'cardapio' && (
          <div className="tablet-sidebar-categorias">
            {categoriasVisiveis.map(cat => (
              <button
                key={cat.id}
                className={`tablet-cat-btn ${String(categoriaAtiva) === String(cat.id) ? 'active' : ''}`}
                onClick={() => scrollToCategoria(cat.id)}
              >
                {cat.nome}
              </button>
            ))}
          </div>
        )}

        <div className="tablet-sidebar-mesa">
          <button onClick={() => setSidebarView('grub')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <img src={require('../assets/powered-by-grub_whte.png')} alt="Powered by GRUB" style={{ width: '120px', opacity: 0.5 }} />
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="tablet-content" ref={contentRef}>
        {/* Header bar */}
        <div className="tablet-header">
          <div className="tablet-header-left">
            <div className="tablet-header-search">
              <Search />
              <input
                type="text"
                placeholder="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm.length >= 2 && (() => {
                const termo = searchTerm.toLowerCase();
                const resultados = categorias.flatMap(cat => (cat.produtos || []))
                  .filter(p => p.nome?.toLowerCase().includes(termo) || p.descricao?.toLowerCase().includes(termo))
                  .slice(0, 8);
                if (resultados.length === 0) return null;
                return (
                  <div className="tablet-search-results">
                    {resultados.map(produto => (
                      <div
                        key={produto.id}
                        className="tablet-search-item"
                        onClick={() => { onProdutoClick(produto); setSearchTerm(''); }}
                      >
                        <div className="tablet-search-item-img" style={{ backgroundImage: `url(${getImageUrl(produto)})` }} />
                        <div className="tablet-search-item-info">
                          <span className="tablet-search-item-nome">{produto.nome}</span>
                          <span className="tablet-search-item-preco">{formatPrice(calculatePrice(produto, isDelivery).valor)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            {mesa?.identificador && (
              <div className="tablet-header-mesa">
                MESA {mesa.identificador}
              </div>
            )}
          </div>
          <div className="tablet-header-actions">
            {onMeusPedidosClick && (
              <button className="tablet-header-btn" onClick={onMeusPedidosClick}>
                <Notes />
                <span>MEUS PEDIDOS</span>
              </button>
            )}
            <button className="tablet-header-btn" onClick={onCarrinhoClick}>
              <Cart />
              <span>CARRINHO{itensCarrinho > 0 ? ` (${itensCarrinho})` : ''}</span>
            </button>
          </div>
        </div>
        {/* Promoções (fullscreen) */}
        {sidebarView === 'promocoes' && bannersDoDevice.length > 0 && (
          <div className="tablet-promocoes">
            <BannerCarousel banners={bannersDoDevice} autoPlayInterval={5000} />
          </div>
        )}

        {/* Cardápio: Destaques + Categorias (mesma página, scroll contínuo) */}
        {sidebarView === 'cardapio' && (
          <>
            {destaques && destaques.length > 0 && (
              <div ref={destaquesRef} className="highlight-section" style={{ backgroundColor: '#111', padding: '1px 12px 30px 12px' }}>
                <div className="produtos-container" style={{ paddingTop: 0, paddingBottom: 0 }}>
                  {destaques.map((destaque, idx) => (
                    destaque.produtos && destaque.produtos.length > 0 && (
                      <ProdutoCarousel
                        key={destaque.id}
                        produtos={destaque.produtos.map(produto => {
                          const priceInfo = calculatePrice(produto, isDelivery);
                          return {
                            ...produto,
                            valor: priceInfo.valor,
                            valor_original: priceInfo.valor_original,
                            em_promocao: priceInfo.em_promocao,
                          };
                        })}
                        onProdutoClick={onProdutoClick}
                        titulo={destaque.nome}
                        titleColor={'#ffffff'}
                        variant={idx === 0 ? 'principal' : idx === 1 ? 'secundario' : 'terciario'}
                      />
                    )
                  ))}
                </div>
              </div>
            )}

            <div ref={cardapioRef} className="tablet-cardapio">
              {categoriasVisiveis.map((categoria) => (
                <section
                  key={categoria.id}
                  ref={(el) => (categoriasRefs.current[categoria.id] = el)}
                  className="tablet-categoria-section"
                >
                  <div className="tablet-categoria-header">
                    <h2 className="tablet-categoria-nome">{categoria.nome}</h2>
                  </div>
                  <div className="tablet-produtos-grid">
                    {(categoria.produtos || []).map((produto) => {
                      const priceInfo = calculatePrice(produto, isDelivery);
                      return (
                        <div
                          key={produto.id}
                          className="tablet-produto-card-grid"
                          onClick={() => onProdutoClick(produto)}
                        >
                          <div className="tablet-produto-img-grid" style={{ backgroundImage: `url(${getImageUrl(produto)})` }}>
                            <div className="tablet-produto-overlay">
                              <span className="tablet-produto-nome-grid">{produto.nome}</span>
                              <span className="tablet-preco-grid">{formatPrice(priceInfo.valor)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}

        {/* GRUB Info */}
        {sidebarView === 'grub' && (
          <div style={{ height: '100%', overflow: 'hidden', background: '#0043FF', position: 'relative' }}>
            {/* Conteúdo superior: logo + texto + tablet */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '48px 40px 0', position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '40px', width: '100%' }}>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <img
                    src={require('../assets/LOGO-GRUB-BRANCO.png')}
                    alt="GRUB"
                    style={{ width: '140px', marginBottom: '20px' }}
                  />
                  <h1 style={{ fontSize: '36px', fontWeight: 700, color: '#fff', margin: '0 0 16px', lineHeight: 1.2, textAlign: 'left' }}>
                    Seu negócio<br />inteligente
                  </h1>
                  <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, margin: 0, textAlign: 'left' }}>
                    Cardápio digital, pedidos por mesa, delivery, PDV, estoque e muito mais.
                  </p>
                  <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: '20px 0 0', textAlign: 'left' }}>
                    Gestão completa do seu restaurante em uma única plataforma. 
                    Controle de estoque, relatórios financeiros, programa de fidelidade, 
                    integração com iFood e muito mais. Simplifique sua operação.
                  </p>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src={require('../assets/tablet-grub.png')}
                    alt="GRUB Tablet"
                    style={{ width: '100%', maxWidth: '400px', objectFit: 'contain' }}
                  />
                </div>
              </div>
            </div>
            {/* Imagem grudada no bottom */}
            <img
              src={require('../assets/licks.png')}
              alt="Depoimento"
              style={{ position: 'fixed', bottom: 0, left: 250, right: 0, width: 'calc(100% - 250px)', display: 'block', zIndex: 2 }}
            />
          </div>
        )}
      </main>
    </div>
  );
}

export default TabletLayout;
